import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { DataManager } from "@/components/DataManager";
import { PageHeader, StatusBadge, DaysBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { ShieldCheck, CheckCircle2, Repeat } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const daysUntil = (d) => { if (!d) return null; return Math.ceil((new Date(d) - new Date()) / 86400000); };

export default function Compliance() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [completing, setCompleting] = useState(null);

  const reload = useCallback(() => api.get("/compliance_tasks").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => {
    reload();
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, [reload]);

  const complete = async (row) => {
    setCompleting(row.id);
    try {
      const { data } = await api.post(`/compliance_tasks/${row.id}/complete`);
      if (data.next_task_id) {
        toast.success(`Completed — next ${row.frequency?.toLowerCase() || ""} occurrence auto-scheduled.`);
      } else {
        toast.success("Marked complete");
      }
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Complete failed"); }
    finally { setCompleting(null); }
  };

  const empOptions = ["All Staff", ...employees.filter((e) => e.status !== "inactive").map((e) => e.name)];
  const fields = [
    { name: "title", label: "Task Title", required: true, full: true },
    { name: "category", label: "Category", type: "select", options: ["Clean Room Validation", "Media Fill", "Competency", "Environmental Monitoring", "Other"], required: true },
    { name: "assigned_to", label: "Assigned To (employee)", type: "select", options: empOptions },
    { name: "frequency", label: "Frequency", type: "select", options: ["Monthly", "Every 6 months", "Annual", "One-time"] },
    { name: "due_date", label: "Due Date", type: "date", required: true },
    { name: "status", label: "Status", type: "select", options: ["open", "completed"], default: "open" },
  ];

  const columns = [
    { name: "title", label: "Task", render: (r) => <span className="font-medium text-slate-800">{r.title}</span> },
    { name: "category", label: "Category" },
    { name: "assigned_to", label: "Assigned To" },
    { name: "frequency", label: "Frequency", render: (r) => r.frequency ? (
      <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Repeat className="w-3 h-3 text-pink-700" /> {r.frequency}</span>
    ) : "—" },
    { name: "due_date", label: "Due", render: (r) => <DaysBadge days={daysUntil(r.due_date)} /> },
    { name: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ...(isAdmin ? [{ name: "complete", label: "", render: (r) => (
      r.status === "completed" ? (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Done</span>
      ) : (
        <Button data-testid={`compliance-complete-${r.id}`} size="sm" variant="ghost"
          disabled={completing === r.id} onClick={() => complete(r)}
          className="h-8 gap-1 text-emerald-700 hover:bg-emerald-50">
          <CheckCircle2 className="w-3.5 h-3.5" /> Complete
        </Button>
      )
    ) }] : []),
  ];

  return (
    <div>
      <PageHeader testid="compliance-header" title="Compliance & Validation"
        subtitle="Complete a recurring task and Vera auto-schedules the next occurrence at its frequency — no manual re-entry." />
      <DataManager testidPrefix="compliance" endpoint="compliance_tasks" fields={fields} columns={columns}
        items={items} reload={reload} emptyIcon={ShieldCheck} emptyTitle="No compliance tasks yet" />
    </div>
  );
}
